/**
 * Settings control in the RMA details column header: opens a popover to pick the list sort
 * direction and which date the column shows. Both selections apply immediately and close the
 * popover - the sort direction is kept in the list store (descending by default), the date
 * field is persisted to UI settings before reloading.
 */
import { observer } from "mobx-react";
import {
    autoUpdate,
    FloatingPortal,
    offset,
    useDismiss,
    useFloating,
    useInteractions,
} from "@floating-ui/react";
import IconSliders from "../../../../../components/icons/icon-sliders/icon-sliders";
import { SORT_TYPES } from "../../../../../components/table/table.types";
import { RETURNS_DATE_FIELD } from "../../../../../services/api/ui-settings-service";
import uiSettingsStore from "../../../../../stores/ui-settings-store";
import rmsMixpanelStore from "../../../../../stores/rms-mixpanel-store";
import rmaPagedListStore, { DATE_SORT_FIELD } from "../../stores/rma-tabs-paged-list-store";
import { useRmaListFilterPopover } from "../rma-list-filter-control/use-rma-list-filter-popover";
import RmaListTableSettingsContent from "./rma-list-table-settings-content/rma-list-table-settings-content";
import "./rma-list-table-settings.scss";

interface IProps {
    disabled?: boolean;
}

const RmaListTableSettings: React.FC<IProps> = observer(({ disabled }) => {
    const { isOpen, close, toggle } = useRmaListFilterPopover({ disabled });

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: (open) => {
            if (!open) {
                close();
            }
        },
        placement: "bottom-start",
        strategy: "fixed",
        middleware: [offset(8)],
        whileElementsMounted: autoUpdate,
    });
    const dismiss = useDismiss(context);
    const { getFloatingProps } = useInteractions([dismiss]);

    const sortDir =
        rmaPagedListStore.sortBy === DATE_SORT_FIELD ? (rmaPagedListStore.sortDir as SORT_TYPES) : null;

    const onSortDirChange = (nextSortDir: SORT_TYPES) => {
        close();
        rmsMixpanelStore.trackEvent("d&_date_column");
        rmaPagedListStore.resetIsLoaded();
        rmaPagedListStore.sortData(DATE_SORT_FIELD, nextSortDir);
    };

    const onDateFieldChange = async (dateField: RETURNS_DATE_FIELD) => {
        close();
        uiSettingsStore.setReturnsDateField(dateField);
        await uiSettingsStore.save();
        rmaPagedListStore.resetIsLoaded();
        rmaPagedListStore.loadPage(rmaPagedListStore.page);
    };

    return (
        <span className="rma-list-table-settings">
            <button
                ref={refs.setReference}
                type="button"
                className="rma-list-table-settings__button"
                aria-expanded={isOpen}
                disabled={disabled}
                onClick={(event) => {
                    event.stopPropagation();
                    toggle();
                }}
            >
                <IconSliders />
            </button>

            {isOpen && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        className="rma-list-table-settings__panel"
                        style={floatingStyles}
                        {...getFloatingProps()}
                    >
                        <RmaListTableSettingsContent
                            sortDir={sortDir}
                            sortOptions={rmaPagedListStore.sortOptions}
                            onSortDirChange={onSortDirChange}
                            dateField={uiSettingsStore.returnsDateField}
                            dateFieldOptions={uiSettingsStore.returnsDateFieldOptions}
                            onDateFieldChange={onDateFieldChange}
                        />
                    </div>
                </FloatingPortal>
            )}
        </span>
    );
});

export default RmaListTableSettings;
